const m = require('minthril');
const { routemeup } = require('routemeup');
const { component } = require('../component');
const { Header } = require('../components/header');
const { Footer } = require('../components/footer');
const { ErrorToast } = require('../components/error-toast');
const { AuthPage } = require('./auth');
const { AdminPage } = require('./admin');
const { AdminUsagePage } = require('./admin-usage');
const { AdminEstimatesPage } = require('./admin-estimates');
const { AdminAccountPage } = require('./admin-account');
const { AccountPage, DeveloperAccessPage } = require('./account');
const { BillingPage } = require('./billing');
const { BillingCancelPage } = require('./billing-cancel');
const { CostPage } = require('./cost');
const { ContactPage } = require('./contact');
const {
  CacheInvalidationDocsPage,
  DocsPage,
  PutYourSiteInSpacheDocsPage,
  ScriptLoadingDocsPage
} = require('./docs');
const { HowSpacheWorksPage } = require('./docs/how-spache-works');
const { AstroComparisonDocsPage } = require('./docs/comparisons/astro');
const { NextComparisonDocsPage } = require('./docs/comparisons/nextjs');
const { ReactSnapComparisonPage } = require('./docs/comparisons/react-snap');
const { PrerenderComparisonPage } = require('./docs/comparisons/prerender');
const { ComparisonsPage } = require('./docs/comparisons');
const { FeaturesDocsPage } = require('./docs/features');
const { PricingDocsPage } = require('./docs/pricing');
const { McpDocsPage } = require('./docs/mcp');
const { PrivacyPage } = require('./privacy');
const { HomePage } = require('./home');
const { PayPage } = require('./pay');
const { AccessDeniedPage, NotFoundPage } = require('./not-found');
const { RouteDetailsPage } = require('./route-details');
const { RouteLogsPage } = require('./route-logs');
const { RouteSettingsPage } = require('./route-settings');
const { RoutesPage } = require('./routes');
const { SettingsPage } = require('./settings');
const { SitePage } = require('./site');
const { SitesPage } = require('./sites');
const { TrafficPage } = require('./traffic');
const { TermsPage } = require('./terms');
const { OAuthAuthorizePage } = require('./oauth-authorize');

function sitesPage(controller) {
  return m(SitesPage, {
    sites: controller.sites(),
    onNewSite: controller.newSite,
    page: controller.siteListPage(),
    hasPrevious: controller.siteListHasPrevious(),
    hasNext: controller.siteListHasNext(),
    loading: controller.siteListLoading(),
    onPageChange: controller.changeSitePage
  });
}

function settingsPage(controller, site, section = 'site') {
  return m(SettingsPage, {
    form: controller.form(),
    site,
    section,
    accountId: controller.user()?.uid,
    loadBillingAccess: controller.billingAccess,
    saving: controller.saving(),
    onClearCache: () => controller.clearCache(site),
    cacheWebhookUrl: site && controller.cacheWebhookUrl(site.id),
    onCreateCacheWebhook: () => controller.createSiteCacheWebhook(site),
    onRevokeCacheWebhook: () => controller.revokeSiteCacheWebhook(site),
    provisioningDomain: site && controller.provisioningSite(site),
    removing: site && controller.removingSite(site),
    onSave: controller.save,
    onChange: controller.changeForm,
    onAddCapture: controller.addFormCapture,
    onAddIgnoredRoute: controller.addFormIgnoredRoute,
    onRemoveIgnoredRoute: controller.removeFormIgnoredRoute,
    onAddDomain: () => controller.addDomain(site),
    onRefreshDomain: domain => controller.refreshDomain(site, domain),
    onTestDomain: domain => controller.testConfiguredDomain(site, domain),
    isDomainProvisioning: domain => controller.provisioningDomain(site, domain),
    isDomainRemoving: domain => controller.removingDomain(site, domain),
    onRemoveDomain: domain => controller.removeConfiguredDomain(site, domain),
    onRemove: () => controller.removeConfiguredSite(site)
  });
}

function routePath() {
  const path = new URLSearchParams(window.location.search).get('route') || '/';
  return new URL(path, window.location.origin).pathname;
}

function routeOptions(stats) {
  const routes = new Map();

  for (const page of stats?.recentlyRenderedPages || []) {
    routes.set(page.path, { path: page.path });
  }

  for (const route of stats?.routes || []) {
    routes.set(route.path, { path: route.path });
  }

  return [...routes.values()];
}

function sitePaths(siteId, isAdmin) {
  const base = `${isAdmin ? '/admin' : ''}/sites/${siteId}`;
  return {
    base,
    overview: base,
    traffic: `${base}/traffic`,
    routes: `${base}/routes`,
    routeDetails: `${base}/routes/route`,
    routeSettings: `${base}/routes/route/settings`,
    cost: `${base}/cost`,
    settings: `${base}/settings`
  };
}

function siteTrafficPage(controller, siteId, isAdmin = false) {
  if (isAdmin && !controller.isAdmin()) return m(AccessDeniedPage);

  const site = controller.prepareRoute({
    name: isAdmin ? 'admin-stats' : 'stats',
    siteId
  });
  if (!site) return m('p.muted', 'Loading site…');

  const paths = sitePaths(site.id, isAdmin);
  return m(TrafficPage, {
    site,
    stats: controller.statsFor(site),
    backHref: isAdmin ? '/admin' : '/sites',
    overviewHref: paths.overview,
    trafficHref: paths.traffic,
    routesHref: paths.routes,
    costHref: paths.cost,
    settingsHref: isAdmin ? undefined : paths.settings,
    onUsagePeriodChange: periodHours => controller.setStatsUsagePeriod(site, periodHours),
    usagePeriodHours: controller.statsUsagePeriodHours()
  });
}

function siteRoutesPage(controller, siteId, isAdmin = false) {
  if (isAdmin && !controller.isAdmin()) return m(AccessDeniedPage);

  const site = controller.prepareRoute({
    name: isAdmin ? 'admin-stats' : 'stats',
    siteId
  });
  if (!site) return m('p.muted', 'Loading site…');

  const paths = sitePaths(site.id, isAdmin);
  const stats = controller.statsFor(site);
  return m(RoutesPage, {
    site,
    stats,
    routes: routeOptions(stats),
    routeDetailsHref: paths.routeDetails,
    backHref: isAdmin ? '/admin' : '/sites',
    overviewHref: paths.overview,
    trafficHref: paths.traffic,
    routesHref: paths.routes,
    costHref: paths.cost,
    settingsHref: isAdmin ? undefined : paths.settings
  });
}

function siteRouteDetailsPage(controller, siteId, isAdmin = false) {
  if (isAdmin && !controller.isAdmin()) return m(AccessDeniedPage);

  const site = controller.prepareRoute({
    name: isAdmin ? 'admin-stats' : 'stats',
    siteId
  });
  if (!site) return m('p.muted', 'Loading site…');

  const paths = sitePaths(site.id, isAdmin);
  const stats = controller.statsFor(site);
  return m(RouteDetailsPage, {
    site,
    path: routePath(),
    recentRoutes: stats?.recentlyRenderedPages || [],
    rendererMetrics: stats?.routes?.find(route => route.path === new URL(routePath(), 'https://spache.local').pathname),
    usagePeriodHours: stats?.routeUsagePeriodHours,
    routes: routeOptions(stats),
    onLoad: path => controller.routeTimeline(site, path),
    onLoadMore: (path, cursor) => controller.routeEventPage(site, path, cursor),
    onLoadCacheInfo: path => controller.routeCacheInfo(site, path),
    onLoadScreenshot: path => controller.routeScreenshot(site, path),
    detailsHref: paths.routeDetails,
    backHref: paths.routes,
    overviewHref: paths.overview,
    trafficHref: paths.traffic,
    routesHref: paths.routes,
    costHref: paths.cost,
    settingsHref: isAdmin ? undefined : paths.settings,
    routeSettingsHref: paths.routeSettings,
    sitesHref: isAdmin ? '/admin' : '/sites',
    onClearCache: path => controller.clearRoute(site, path)
  });
}

function siteRouteLogsPage(controller, siteId, isAdmin = false) {
  if (isAdmin && !controller.isAdmin()) return m(AccessDeniedPage);

  const site = controller.prepareRoute({
    name: isAdmin ? 'admin-stats' : 'stats',
    siteId
  });
  if (!site) return m('p.muted', 'Loading site…');

  const paths = sitePaths(site.id, isAdmin);
  const path = routePath();
  return m(RouteLogsPage, {
    site,
    path,
    routes: routeOptions(controller.statsFor(site)),
    onLoad: route => controller.routeEvents(site, route),
    sitesHref: isAdmin ? '/admin' : '/sites',
    overviewHref: paths.overview,
    routesHref: paths.routes,
    detailsHref: paths.routeDetails
  });
}

function siteRouteSettingsPage(controller, siteId, isAdmin = false) {
  if (isAdmin && !controller.isAdmin()) return m(AccessDeniedPage);

  const site = controller.prepareRoute({
    name: isAdmin ? 'admin-stats' : 'route-settings',
    siteId
  });
  if (!site) return m('p.muted', 'Loading site…');

  const paths = sitePaths(site.id, isAdmin);
  const stats = controller.statsFor(site);
  return m(RouteSettingsPage, {
    site,
    path: routePath(),
    routes: routeOptions(stats),
    rendererMetrics: stats?.routes?.find(route => route.path === new URL(routePath(), 'https://spache.local').pathname),
    usagePeriodHours: stats?.routeUsagePeriodHours,
    detailsHref: paths.routeDetails,
    backHref: `${paths.routeDetails}?route=${encodeURIComponent(routePath())}`,
    overviewHref: paths.overview,
    trafficHref: paths.traffic,
    routesHref: paths.routes,
    costHref: paths.cost,
    settingsHref: isAdmin ? undefined : paths.settings,
    sitesHref: isAdmin ? '/admin' : '/sites',
    onLoad: path => controller.routeCacheInfo(site, path),
    onUpdate: (path, parameter, action) => controller.updateRouteSettings(site, path, parameter, action),
    onUpdateScreenshotVisibility: (path, isPublic) =>
      controller.updateRouteScreenshot(site, path, isPublic),
    onUpdateRenderer: (path, renderer) => controller.updateRouteRenderer(site, path, renderer)
  });
}

function pageRoutes(controller) {
  return {
    '/oauth/authorize': () => m(OAuthAuthorizePage, {
      user: controller.user(),
      onSignIn: () => controller.signIn()
    }),
    '/': () => {
      controller.prepareRoute({ name: 'home' });
      return m(HomePage, {
        user: controller.user(),
        onSignIn: () => controller.signIn('/sites')
      });
    },
    '/app': () => {
      controller.prepareRoute({ name: 'sites' });
      return sitesPage(controller);
    },
    '/sites': () => {
      controller.prepareRoute({ name: 'sites' });
      return sitesPage(controller);
    },
    '/admin': () => {
      controller.prepareRoute({ name: 'admin' });
      return controller.isAdmin() ? m(AdminPage, {
        user: controller.user(),
        onStartFreshAccountPreview: controller.startFreshAccountPreview,
        startingFreshAccountPreview: controller.startingFreshAccountPreview()
      }) : m(AccessDeniedPage);
    },
    '/admin/usage': () => {
      controller.prepareRoute({ name: 'admin-usage' });
      return controller.isAdmin()
        ? m(AdminUsagePage, { user: controller.user() })
        : m(AccessDeniedPage);
    },
    '/admin/estimates': () => {
      controller.prepareRoute({ name: 'admin-estimates' });
      return controller.isAdmin()
        ? m(AdminEstimatesPage, { user: controller.user() })
        : m(AccessDeniedPage);
    },
    '/billing': () => {
      controller.prepareRoute({ name: 'billing' });
      return m(BillingPage, { user: controller.user(), showAccountNavigation: true });
    },
    '/account': () => {
      controller.prepareRoute({ name: 'account' });
      return m(AccountPage, { user: controller.user() });
    },
    '/account/billing': () => {
      controller.prepareRoute({ name: 'billing' });
      return m(BillingPage, {
        user: controller.user(),
        breadcrumbItems: [{ label: 'Account', href: '/account' }, { label: 'Billing' }],
        showAccountNavigation: true
      });
    },
    '/account/developer': () => {
      controller.prepareRoute({ name: 'account-developer' });
      return m(DeveloperAccessPage, { user: controller.user() });
    },
    '/billing/cancel': () => {
      controller.prepareRoute({ name: 'billing-cancel' });
      return m(BillingCancelPage, { user: controller.user() });
    },
    '/account/billing/cancel': () => {
      controller.prepareRoute({ name: 'billing-cancel' });
      return m(BillingCancelPage, { user: controller.user(), accountPath: '/account/billing' });
    },
    '/pay': () => {
      controller.prepareRoute({ name: 'pay' });
      return m(PayPage);
    },
    '/terms': () => {
      controller.prepareRoute({ name: 'terms' });
      return m(TermsPage);
    },
    '/privacy': () => {
      controller.prepareRoute({ name: 'privacy' });
      return m(PrivacyPage);
    },
    '/contact': () => {
      controller.prepareRoute({ name: 'contact' });
      return m(ContactPage);
    },
    '/docs': () => {
      controller.prepareRoute({ name: 'docs' });
      return m(DocsPage);
    },
    '/docs/mcp': () => {
      controller.prepareRoute({ name: 'docs-mcp' });
      return m(McpDocsPage);
    },
    '/docs/cache-invalidation': () => {
      controller.prepareRoute({ name: 'docs-cache-invalidation' });
      return m(CacheInvalidationDocsPage);
    },
    '/docs/put-your-site-in-spache': () => {
      controller.prepareRoute({ name: 'docs-put-your-site-in-spache' });
      return m(PutYourSiteInSpacheDocsPage);
    },
    '/docs/how-spache-works': () => {
      controller.prepareRoute({ name: 'docs-how-spache-works' });
      return m(HowSpacheWorksPage);
    },
    '/how-it-works': () => {
      controller.prepareRoute({ name: 'docs-how-spache-works' });
      return m(HowSpacheWorksPage);
    },
    '/docs/script-loading': () => {
      controller.prepareRoute({ name: 'docs-script-loading' });
      return m(ScriptLoadingDocsPage);
    },
    '/docs/comparisons/astro': () => {
      controller.prepareRoute({ name: 'docs-comparison-astro' });
      return m(AstroComparisonDocsPage);
    },
    '/docs/comparisons': () => {
      controller.prepareRoute({ name: 'docs-comparisons' });
      return m(ComparisonsPage);
    },
    '/docs/comparisons/nextjs': () => {
      controller.prepareRoute({ name: 'docs-comparison-nextjs' });
      return m(NextComparisonDocsPage);
    },
    '/docs/comparisons/react-snap': () => {
      controller.prepareRoute({ name: 'docs-comparison-react-snap' });
      return m(ReactSnapComparisonPage);
    },
    '/docs/comparisons/prerender': () => {
      controller.prepareRoute({ name: 'docs-comparison-prerender' });
      return m(PrerenderComparisonPage);
    },
    '/docs/features': () => {
      controller.prepareRoute({ name: 'docs-features' });
      return m(FeaturesDocsPage);
    },
    '/docs/pricing': () => {
      controller.prepareRoute({ name: 'docs-pricing' });
      return m(PricingDocsPage);
    },
    '/admin/accounts/:accountId/billing': ({ accountId }) => {
      if (!controller.isAdmin()) {
        return m(AccessDeniedPage);
      }

      return m(BillingPage, {
        user: controller.user(),
        loadSummary: user => controller.accountBilling(user, accountId),
        breadcrumbItems: [
          { label: 'Admin', href: '/admin' },
          { label: 'Account billing' }
        ],
        heading: 'Account billing',
        allowCheckout: false,
        loadKey: accountId
      });
    },
    '/admin/accounts/:accountId': ({ accountId }) => {
      if (!controller.isAdmin()) {
        return m(AccessDeniedPage);
      }

      return m(AdminAccountPage, {
        user: controller.user(),
        accountId,
        onLoadBilling: () => controller.accountBilling(accountId),
        onUpdateFreeTier: (mode, billingTestEnabled, freeTier) =>
          controller.updateAccountFreeTier(accountId, mode, billingTestEnabled, freeTier),
        onGrantDomainTrial: () => controller.grantAccountDomainTrial(accountId),
        onImpersonate: targetAccountId => controller.impersonate(targetAccountId)
      });
    },
    '/admin/sites/:siteId/routes/route/settings': ({ siteId }) =>
      siteRouteSettingsPage(controller, siteId, true),
    '/admin/sites/:siteId/routes/route/logs': ({ siteId }) =>
      siteRouteLogsPage(controller, siteId, true),
    '/admin/sites/:siteId/routes/route': ({ siteId }) =>
      siteRouteDetailsPage(controller, siteId, true),
    '/admin/sites/:siteId/routes': ({ siteId }) => siteRoutesPage(controller, siteId, true),
    '/admin/sites/:siteId/traffic': ({ siteId }) => siteTrafficPage(controller, siteId, true),
    '/admin/sites/:siteId/stats/route/settings': ({ siteId }) =>
      siteRouteSettingsPage(controller, siteId, true),
    '/admin/sites/:siteId/stats/route': ({ siteId }) =>
      siteRouteDetailsPage(controller, siteId, true),
    '/admin/sites/:siteId/stats': ({ siteId }) => siteTrafficPage(controller, siteId, true),
    '/admin/sites/:siteId/cost': ({ siteId }) => {
      if (!controller.isAdmin()) {
        return m(AccessDeniedPage);
      }

      const site = controller.prepareRoute({ name: 'admin-cost', siteId });
      return site ? m(CostPage, {
        site,
        stats: controller.statsFor(site),
        backHref: '/admin',
        overviewHref: `/admin/sites/${site.id}`,
        trafficHref: `/admin/sites/${site.id}/traffic`,
        routesHref: `/admin/sites/${site.id}/routes`,
        costHref: `/admin/sites/${site.id}/cost`,
        onLoadSpend: () => controller.spendEstimate(site)
      }) : m('p.muted', 'Loading site…');
    },
    '/admin/sites/:siteId': ({ siteId }) => {
      if (!controller.isAdmin()) {
        return m(AccessDeniedPage);
      }

      const site = controller.prepareRoute({ name: 'admin-site', siteId });
      return site ? m(SitePage, {
        site,
        stats: controller.statsFor(site),
        backHref: '/admin',
        overviewHref: `/admin/sites/${site.id}`,
        trafficHref: `/admin/sites/${site.id}/traffic`,
        routesHref: `/admin/sites/${site.id}/routes`,
        costHref: `/admin/sites/${site.id}/cost`,
        onLoadSpend: () => controller.spendEstimate(site)
      }) : m('p.muted', 'Loading site…');
    },
    '/sites/new': () => {
      const pageUrl = new URL(window.location.href);
      const origin = pageUrl.searchParams.get('origin');
      if (origin) {
        pageUrl.searchParams.delete('origin');
        window.history.replaceState({}, '', `${pageUrl.pathname}${pageUrl.search}${pageUrl.hash}`);
      }

      controller.prepareRoute({
        name: 'new-site',
        origin
      });
      return controller.canCreateSites() ? settingsPage(controller) : m(AccessDeniedPage);
    },
    '/sites/:siteId/settings': ({ siteId }) => {
      const site = controller.prepareRoute({ name: 'settings', siteId });
      return site && settingsPage(controller, site);
    },
    '/sites/:siteId/settings/domains': ({ siteId }) => {
      const site = controller.prepareRoute({ name: 'settings', siteId });
      return site && settingsPage(controller, site, 'domains');
    },
    '/sites/:siteId/settings/rendering': ({ siteId }) => {
      const site = controller.prepareRoute({ name: 'settings', siteId });
      return site && settingsPage(controller, site, 'rendering');
    },
    '/sites/:siteId/settings/cache': ({ siteId }) => {
      const site = controller.prepareRoute({ name: 'settings', siteId });
      return site && settingsPage(controller, site, 'cache');
    },
    '/sites/:siteId/settings/captures': ({ siteId }) => {
      const site = controller.prepareRoute({ name: 'settings', siteId });
      return site && settingsPage(controller, site, 'captures');
    },
    '/sites/:siteId/settings/webhooks': ({ siteId }) => {
      const site = controller.prepareRoute({ name: 'settings', siteId });
      return site && settingsPage(controller, site, 'webhooks');
    },
    '/sites/:siteId/settings/maintenance': ({ siteId }) => {
      const site = controller.prepareRoute({ name: 'settings', siteId });
      return site && settingsPage(controller, site, 'maintenance');
    },
    '/sites/:siteId/routes/route/settings': ({ siteId }) =>
      siteRouteSettingsPage(controller, siteId),
    '/sites/:siteId/routes/route/logs': ({ siteId }) =>
      siteRouteLogsPage(controller, siteId),
    '/sites/:siteId/routes/route': ({ siteId }) => siteRouteDetailsPage(controller, siteId),
    '/sites/:siteId/routes': ({ siteId }) => siteRoutesPage(controller, siteId),
    '/sites/:siteId/traffic': ({ siteId }) => siteTrafficPage(controller, siteId),
    '/sites/:siteId/stats/route/settings': ({ siteId }) =>
      siteRouteSettingsPage(controller, siteId),
    '/sites/:siteId/stats/route': ({ siteId }) => siteRouteDetailsPage(controller, siteId),
    '/sites/:siteId/stats': ({ siteId }) => siteTrafficPage(controller, siteId),
    '/sites/:siteId/cost': ({ siteId }) => {
      const site = controller.prepareRoute({ name: 'cost', siteId });
      return site && m(CostPage, {
        site,
        stats: controller.statsFor(site),
        backHref: '/sites',
        overviewHref: `/sites/${site.id}`,
        trafficHref: `/sites/${site.id}/traffic`,
        routesHref: `/sites/${site.id}/routes`,
        costHref: `/sites/${site.id}/cost`,
        settingsHref: `/sites/${site.id}/settings`,
        onRefreshDomain: domain => controller.refreshDomain(site, domain),
        onLoadSpend: () => controller.spendEstimate(site)
      });
    },
    '/sites/:siteId': ({ siteId }) => {
      const site = controller.prepareRoute({ name: 'site', siteId });
      return site && m(SitePage, {
        site,
        stats: controller.statsFor(site),
        trafficHref: `/sites/${site.id}/traffic`,
        routesHref: `/sites/${site.id}/routes`,
        costHref: `/sites/${site.id}/cost`,
        settingsHref: `/sites/${site.id}/settings`,
        onLoadSpend: () => controller.spendEstimate(site)
      });
    }
  };
}

function selectedPage(controller) {
  const route = routemeup(pageRoutes(controller), { url: window.location.pathname });

  if (route) {
    return route.controller(route.tokens);
  }

  controller.prepareRoute({ name: 'not-found' });
  return m(NotFoundPage);
}

function oauthAuthorizationPage(controller, requiresTermsAcceptance) {
  if (requiresTermsAcceptance) {
    return m('section.dashboard', m(TermsPage, {
      acceptanceRequired: true,
      accepting: controller.acceptingTerms(),
      onAccept: controller.acceptTerms
    }));
  }
  return m('section.dashboard', selectedPage(controller));
}

const PageRouter = component(() => {
  return {
    view: ({ attrs: { controller } }) => {
      const user = controller.user();

      const isMarketingPage = window.location.pathname === '/';
      const isPaymentPage = window.location.pathname === '/pay';
      const isTermsPage = window.location.pathname === '/terms';
      const isPrivacyPage = window.location.pathname === '/privacy';
      const isContactPage = window.location.pathname === '/contact';
      const isOAuthAuthorizationPage = window.location.pathname === '/oauth/authorize';
      const isDocsPage = window.location.pathname === '/docs' ||
        window.location.pathname === '/docs/mcp' ||
        window.location.pathname === '/docs/cache-invalidation' ||
        window.location.pathname === '/docs/put-your-site-in-spache' ||
        window.location.pathname === '/docs/how-spache-works' ||
        window.location.pathname === '/how-it-works' ||
        window.location.pathname === '/docs/script-loading' ||
        window.location.pathname === '/docs/comparisons' ||
        window.location.pathname === '/docs/comparisons/astro' ||
        window.location.pathname === '/docs/comparisons/nextjs' ||
        window.location.pathname === '/docs/comparisons/react-snap' ||
        window.location.pathname === '/docs/comparisons/prerender' ||
        window.location.pathname === '/docs/features' ||
        window.location.pathname === '/docs/pricing';
      const requiresTermsAcceptance = user && controller.termsAccepted() === false &&
        !controller.impersonating();
      const dashboardWidth = isDocsPage ? '.dashboard--wide' : '';

      return m('main.app',
        [
          m(Header, {
            user,
            isAdmin: controller.isAdmin(),
            impersonating: controller.impersonating(),
            impersonatedAccount: controller.impersonatedAccount(),
            freshAccountPreview: controller.freshAccountPreview(),
            stoppingFreshAccountPreview: controller.stoppingFreshAccountPreview(),
            onSignIn: controller.signIn,
            onSignOut: controller.signOut,
            onStopImpersonation: controller.stopImpersonation,
            onStopFreshAccountPreview: controller.stopFreshAccountPreview
          }),
          controller.error() && m(ErrorToast, {
            message: controller.error(),
            onDismiss: controller.dismissError
          }),
            isMarketingPage ? selectedPage(controller) :
            isTermsPage || isPrivacyPage || isContactPage || isDocsPage ? m('section.dashboard' + dashboardWidth, selectedPage(controller)) :
            isPaymentPage ? m('section.dashboard', selectedPage(controller)) :
            isOAuthAuthorizationPage
              ? oauthAuthorizationPage(controller, requiresTermsAcceptance)
              :
            controller.authResolved() && (user ?
              requiresTermsAcceptance
                ? m('section.dashboard', m(TermsPage, {
                  acceptanceRequired: true,
                  accepting: controller.acceptingTerms(),
                  onAccept: controller.acceptTerms
                }))
                : m('section.dashboard', selectedPage(controller)) :
              m(AuthPage, { onSignIn: controller.signIn })),
          m(Footer)
        ]
      );
    }
  };
});

module.exports = { PageRouter };
